/**
 * Which required profile fields an account is still missing.
 *
 * `backend/services/profile_completeness.py` is the AUTHORITY — it refuses
 * every authenticated endpoint with 403 `profile_incomplete` until these fields
 * are set. This file exists so the app can render the right form *before* the
 * user walks into that wall, and so the wall never appears without an
 * explanation attached. If the two ever disagree, the server wins and the user
 * sees a 403 they can't act on, so keep them in step.
 *
 * Why a server rule at all (founder, 2026-08-04: *"can you completely lock out
 * users so they can not create an account without full name, age and sex? we
 * talked about this many times"*): every previous attempt lived in the UI, and
 * the account row is created on the first authenticated request — before any
 * screen has rendered. A rule that runs after the row exists guards a screen,
 * not a database.
 */

/** Mirrors MIN_AGE_YEARS in the Python module. Under-13 is a COPPA line. */
export const MIN_AGE_YEARS = 13;
export const MIN_BIRTH_YEAR = 1900;

/** Male/Female only — founder decision 2026-08-04. Mirrors
 *  SATISFYING_SEX_VALUES in the Python module. An account already holding
 *  "prefer_not_to_say" therefore reads as incomplete and is asked again. */
export const SATISFYING_SEX_VALUES = new Set(["male", "female"]);

export type RequiredField = "first_name" | "last_name" | "birth_year" | "gender";

export const FIELD_LABELS: Record<RequiredField, string> = {
  first_name: "first name",
  last_name: "last name",
  birth_year: "year of birth",
  gender: "sex",
};

export interface ProfileFields {
  firstName?: string | null;
  lastName?: string | null;
  birth_year?: number | null;
  gender?: string | null;
}

export function birthYearIsValid(
  year: number | string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (year === null || year === undefined || year === "") return false;
  const parsed = Number(year);
  if (!Number.isInteger(parsed)) return false;
  return parsed >= MIN_BIRTH_YEAR && parsed <= now.getFullYear() - MIN_AGE_YEARS;
}

/** In the order they're asked for, so the form can focus the first one. */
export function missingFields(
  user: ProfileFields | null | undefined,
  now: Date = new Date(),
): RequiredField[] {
  if (!user) return [];
  const missing: RequiredField[] = [];
  if (!user.firstName?.trim()) missing.push("first_name");
  if (!user.lastName?.trim()) missing.push("last_name");
  if (!birthYearIsValid(user.birth_year, now)) missing.push("birth_year");
  if (!SATISFYING_SEX_VALUES.has((user.gender ?? "").trim().toLowerCase())) missing.push("gender");
  return missing;
}

export function isComplete(user: ProfileFields | null | undefined, now: Date = new Date()): boolean {
  return missingFields(user, now).length === 0;
}

/** "first name, last name and sex" — for a sentence a person reads. */
export function describeMissing(missing: RequiredField[]): string {
  const labels = missing.map((f) => FIELD_LABELS[f]);
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * First value that actually has something in it.
 *
 * Exists because `a ?? b` is the wrong operator for prefilling a form from
 * "draft, then server": nullish coalescing stops at an EMPTY STRING, so a
 * stored draft of `""` beats the real name the server holds — and keeps
 * beating it on every future mount. That is the founder's 2026-08-10 report
 * (the gate reopening with First/Last blank while Year and Sex were filled)
 * and the earlier "I had to fill out my full name again" reports.
 *
 * Whitespace counts as empty: a draft holding `" "` is not an answer.
 */
export function preferFilled(...values: (string | null | undefined)[]): string {
  for (const v of values) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
}

/**
 * Should the blocking "One quick thing" form be shown?
 *
 * SERVER FIRST, and that is the whole point. `GET /api/users/me` now returns
 * `profileMissingFields`, computed by `backend/services/profile_completeness.py`
 * — the same module that decides whether to 403 a request. When that list is
 * present we use it and never second-guess it, in EITHER direction:
 *
 *   • empty list  → the server is happy → no form, even if `missingFields()`
 *     below disagrees. This is the fix for the popup that kept returning on an
 *     account the server considered complete (founder, 2026-08-10 — the app
 *     behind the modal was fully rendered, which is impossible if anything
 *     were 403ing `profile_incomplete`).
 *   • non-empty   → the server really is refusing requests, so the form is the
 *     only useful thing on screen. Show it, and show ITS list, so the sentence
 *     names the fields the server is actually waiting on.
 *
 * `missingFields()` remains only as the fallback for a payload without the
 * key — an older cached bundle mid-update, or a response restored from the
 * persisted query cache before the first fresh fetch lands.
 *
 * The freshness requirement stays either way: an unskippable form must never
 * be raised off a stale copy of the account.
 */
export function profileGateDecision(
  user: (ProfileFields & { profileMissingFields?: string[] | null }) | null | undefined,
  opts: { isGuest: boolean; fetchedFresh: boolean },
  now: Date = new Date(),
): { show: boolean; missing: RequiredField[] } {
  if (opts.isGuest || !user || !opts.fetchedFresh) return { show: false, missing: [] };

  const fromServer = user.profileMissingFields;
  if (Array.isArray(fromServer)) {
    const known = fromServer.filter((f): f is RequiredField => f in FIELD_LABELS);
    return { show: known.length > 0, missing: known };
  }

  const local = missingFields(user, now);
  return { show: local.length > 0, missing: local };
}
